import { Controller } from "@hotwired/stimulus";

// Handles the Line Items "Import" modal without a nested <form>. Browsers
// flatten nested forms, so a <form> inside the project form would still submit
// the outer one. Instead, this controller submits the upload via fetch and
// renders either a turbo-frame (preview) or a turbo-stream (commit) response.
export default class extends Controller {
  static targets = ["file", "previewUrl", "previewContainer", "filePreview", "commitButton"];

  fileSelected() {
    const file = this.fileTarget.files[0];
    if (!file) {
      this.reset();
      return;
    }
    this.filePreviewTarget.innerHTML = this.fileRow(file);
    this.runPreview(file);
  }

  clearFile(event) {
    event.preventDefault();
    this.reset();
  }

  async runPreview(file) {
    const formData = new FormData();
    formData.append("file", file);
    this.previewContainerTarget.innerHTML = this.loadingIndicator();
    await this.submit(this.previewUrlTarget.value, formData);
  }

  async commit(event) {
    event.preventDefault();
    const formData = new FormData();
    formData.append("import_token", this.commitButtonTarget.dataset.importToken);

    const ok = await this.submit(this.commitButtonTarget.dataset.commitUrl, formData);
    if (ok) {
      this.reset();
      $(this.element).modal("hide");
    }
  }

  async submit(url, formData) {
    let response;
    try {
      response = await fetch(url, { method: "POST", body: formData, headers: this.requestHeaders() });
    } catch (_e) {
      this.renderFallbackError();
      return false;
    }
    const html = await response.text();
    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("text/vnd.turbo-stream.html")) {
      window.Turbo.renderStreamMessage(html);
    } else if (contentType.includes("text/html")) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newFrame = doc.querySelector("turbo-frame#line_item_import_preview");
      if (newFrame) {
        this.previewContainerTarget.dataset.importToken = newFrame.dataset.importToken || "";
        this.previewContainerTarget.dataset.validCount = newFrame.dataset.validCount || "0";
        this.previewContainerTarget.innerHTML = newFrame.innerHTML;
      } else {
        this.previewContainerTarget.innerHTML = html;
      }
    } else {
      this.renderFallbackError();
      return false;
    }
    this.syncCommitButton();
    return response.ok;
  }

  syncCommitButton() {
    const token = this.previewContainerTarget.dataset.importToken || "";
    const validCount = parseInt(this.previewContainerTarget.dataset.validCount || "0", 10);
    this.commitButtonTarget.dataset.importToken = token;
    this.commitButtonTarget.hidden = !token || validCount === 0;
  }

  requestHeaders() {
    const headers = { Accept: "text/vnd.turbo-stream.html, text/html" };
    const csrf = document.querySelector("meta[name=csrf-token]");
    if (csrf) headers["X-CSRF-Token"] = csrf.content;
    return headers;
  }

  renderFallbackError() {
    const message = (window.I18n && window.I18n["views.job_line_items.shared.import.request_failed"]) ||
      "Something went wrong processing the import. Please try again.";
    this.previewContainerTarget.innerHTML = `<div class="alert alert-danger">${this.escape(message)}</div>`;
    delete this.previewContainerTarget.dataset.importToken;
    delete this.previewContainerTarget.dataset.validCount;
    this.syncCommitButton();
  }

  reset() {
    this.fileTarget.value = "";
    this.filePreviewTarget.innerHTML = "";
    this.previewContainerTarget.innerHTML = "";
    delete this.previewContainerTarget.dataset.importToken;
    delete this.previewContainerTarget.dataset.validCount;
    this.syncCommitButton();
  }

  loadingIndicator() {
    return `<div class="text-center sm-padding-top sm-padding-bottom"><i class="fa fa-spinner fa-spin"></i></div>`;
  }

  fileRow(file) {
    const sizeKb = (file.size / 1024).toFixed(1);
    return `
      <div class="file-row dz-success">
        <div><p class="name">${this.escape(file.name)}</p></div>
        <div><p class="size">${sizeKb} KB</p></div>
        <div>
          <a class="delete dz-remove" data-action="click->line-item-import#clearFile">
            <i class="fa fa-times"></i>
          </a>
        </div>
      </div>
    `;
  }

  escape(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }
}
